import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Shield20Regular, Phone20Regular, LockClosed20Regular } from '@fluentui/react-icons';
import SwitchToPasswordModal from './SwitchToPasswordModal';
import axiosInstance from '../../apiServices/axios';
import { resolveActiveMfaMethod } from '../../utils/mfaMethod';
import styles from './css/UserSettings.module.css';

const MicrosoftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
        <rect x="1" y="1" width="6.5" height="6.5" fill="#F25022" />
        <rect x="8.5" y="1" width="6.5" height="6.5" fill="#7FBA00" />
        <rect x="1" y="8.5" width="6.5" height="6.5" fill="#00A4EF" />
        <rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#FFB900" />
    </svg>
);

const SecuritySection = ({ onOpenChangePassword, onOpenSSOUnlink, onOpenSetup2FA }) => {
    const { user } = useSelector(state => state.user);
    const [isLinking, setIsLinking] = useState(false);
    const [showSwitchToPasswordModal, setShowSwitchToPasswordModal] = useState(false);

    const isLinked = user?.microsoft_linked;
    const hasMfa = Boolean(user?.has_mfa);
    const activeMfaMethod = hasMfa ? resolveActiveMfaMethod(user?.mfa_method) : null;

    const handleLinkMicrosoft = async () => {
        setIsLinking(true);
        try {
            const response = await axiosInstance.post('accounts/settings/link-azure/');
            if (response.data.azure_auth_url) {
                window.location.href = response.data.azure_auth_url;
            } else {
                throw new Error('No Azure auth URL received from server');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to initiate Microsoft account linking';
            toast.error(errorMessage);
            setIsLinking(false);
        }
    };

    const handleUsePasswordInstead = () => {
        onOpenSSOUnlink(true);
    };

    const handleSetup2FA = () => {
        onOpenSetup2FA(Boolean(hasMfa));
    };

    const renderActiveBadge = () => (
        <span className={styles.activeBadge}>
            <span className={styles.activeBadgeDot} aria-hidden />
            Active
        </span>
    );

    return (
        <>
            <header className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Security &amp; Sign-in</h1>
                <p className={styles.pageDescription}>Manage how you sign in to Lens and configure two-factor authentication.</p>
            </header>

            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Two-factor authentication</h2>
                    <p className={styles.cardCaption}>Choose how Lens verifies your identity when you sign in.</p>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.optionRows}>
                        <div
                            className={
                                activeMfaMethod === 'app'
                                    ? `${styles.optionRow} ${styles.optionRowActive}`
                                    : styles.optionRow
                            }>
                            <div className={styles.optionMain}>
                                <span className={activeMfaMethod === 'app' ? `${styles.optionIcon} ${styles.optionIconActive}` : styles.optionIcon}>
                                    <Shield20Regular aria-hidden />
                                </span>
                                <div>
                                    <p className={styles.optionTitle}>Authenticator app</p>
                                    <p className={styles.optionSubtitle}>Google Authenticator, Authy, or similar</p>
                                </div>
                            </div>
                            {activeMfaMethod === 'app' ? renderActiveBadge() : null}
                        </div>

                        <div
                            className={
                                activeMfaMethod === 'sms'
                                    ? `${styles.optionRow} ${styles.optionRowActive}`
                                    : styles.optionRow
                            }>
                            <div className={styles.optionMain}>
                                <span className={activeMfaMethod === 'sms' ? `${styles.optionIcon} ${styles.optionIconActive}` : styles.optionIcon}>
                                    <Phone20Regular aria-hidden />
                                </span>
                                <div>
                                    <p className={styles.optionTitle}>SMS</p>
                                    <p className={styles.optionSubtitle}>Verification code via text message</p>
                                </div>
                            </div>
                            {activeMfaMethod === 'sms' ? renderActiveBadge() : null}
                        </div>
                    </div>

                    <button type="button" className={styles.secondaryButton} onClick={handleSetup2FA}>
                        {hasMfa ? 'Update 2FA' : 'Set up 2FA'}
                    </button>
                </div>
            </section>

            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Sign-in method</h2>
                    <p className={styles.cardCaption}>You have one active sign-in method.</p>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.optionRows}>
                        <div className={isLinked ? `${styles.optionRow} ${styles.optionRowActive}` : styles.optionRow}>
                            <div className={styles.optionMain}>
                                <span className={styles.optionIcon}>
                                    <MicrosoftIcon />
                                </span>
                                <div>
                                    <p className={styles.optionTitle}>Microsoft Single Sign-On + 2FA</p>
                                    <p className={styles.optionSubtitle}>Sign in with your Microsoft account</p>
                                </div>
                            </div>
                            {isLinked ? (
                                renderActiveBadge()
                            ) : (
                                <button type="button" className={styles.secondaryButton} onClick={handleLinkMicrosoft} disabled={isLinking}>
                                    {isLinking ? 'Linking…' : 'Link Account'}
                                </button>
                            )}
                        </div>

                        <div className={!isLinked ? `${styles.optionRow} ${styles.optionRowActive}` : styles.optionRow}>
                            <div className={styles.optionMain}>
                                <span className={!isLinked ? `${styles.optionIcon} ${styles.optionIconActive}` : styles.optionIcon}>
                                    <LockClosed20Regular aria-hidden />
                                </span>
                                <div>
                                    <p className={styles.optionTitle}>Password + 2FA</p>
                                    <p className={styles.optionSubtitle}>Sign in with your email and password</p>
                                </div>
                            </div>
                            {!isLinked ? (
                                renderActiveBadge()
                            ) : (
                                <button type="button" className={styles.secondaryButton} onClick={() => setShowSwitchToPasswordModal(true)}>
                                    Switch to Password
                                </button>
                            )}
                        </div>
                    </div>

                    {!isLinked ? (
                        <button type="button" className={styles.secondaryButton} onClick={onOpenChangePassword}>
                            Change Password
                        </button>
                    ) : null}
                </div>
            </section>

            <SwitchToPasswordModal
                isOpen={showSwitchToPasswordModal}
                onClose={() => setShowSwitchToPasswordModal(false)}
                onConfirm={handleUsePasswordInstead}
            />
        </>
    );
};

export default SecuritySection;
